import { useCallback, useEffect, useRef, useState } from "react";
import { Globe, Check } from "lucide-react";

// Google Translate integration — free, no quota, translates entire page on the fly.
const LANGUAGES = [
  { code: "it", label: "Italiano", flag: "🇮🇹" },
  { code: "en", label: "English", flag: "🇬🇧" },
  { code: "fr", label: "Français", flag: "🇫🇷" },
  { code: "es", label: "Español", flag: "🇪🇸" },
  { code: "pt", label: "Português", flag: "🇵🇹" },
  { code: "de", label: "Deutsch", flag: "🇩🇪" },
  { code: "ru", label: "Русский", flag: "🇷🇺" },
  { code: "ja", label: "日本語", flag: "🇯🇵" },
  { code: "ar", label: "العربية", flag: "🇸🇦" },
  { code: "zh-CN", label: "中文", flag: "🇨🇳" },
  { code: "nl", label: "Nederlands", flag: "🇳🇱" },
];

const setGoogTransCookie = (lang) => {
  const value = lang === "it" ? "/it/it" : `/it/${lang}`;
  document.cookie = `googtrans=${value};path=/`;
  const host = window.location.hostname;
  if (host.split(".").length >= 2) {
    const parent = "." + host.split(".").slice(-2).join(".");
    document.cookie = `googtrans=${value};path=/;domain=${parent}`;
  }
};

const ensureWidgetLoaded = () => {
  if (window.__gtLoaded) return;
  window.__gtLoaded = true;
  if (!document.getElementById("google_translate_element")) {
    const div = document.createElement("div");
    div.id = "google_translate_element";
    div.style.display = "none";
    document.body.appendChild(div);
  }
  window.googleTranslateElementInit = () => {
    /* eslint-disable-next-line no-undef */
    new window.google.translate.TranslateElement(
      { pageLanguage: "it", includedLanguages: LANGUAGES.map((l) => l.code).join(","), autoDisplay: false },
      "google_translate_element",
    );
  };
  const s = document.createElement("script");
  s.src = "https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";
  s.async = true;
  document.head.appendChild(s);
};

const readCurrentLang = () => {
  const m = document.cookie.match(/googtrans=\/it\/([a-zA-Z\-]+)/);
  return m ? m[1] : "it";
};

const autoDetectOnce = () => {
  try {
    if (localStorage.getItem("simono_autolang_done")) return null;
    localStorage.setItem("simono_autolang_done", "1");
    const langs = navigator.languages || [navigator.language || "it"];
    for (const raw of langs) {
      const lower = (raw || "").toLowerCase();
      const direct = LANGUAGES.find((l) => l.code.toLowerCase() === lower);
      if (direct) {
        // Keep LangContext dictionary aligned with Google Translate target
        localStorage.setItem("simono_lang", direct.code === "en" ? "en" : "it");
        return direct.code;
      }
      const base = lower.split("-")[0];
      const m = LANGUAGES.find((l) => l.code.toLowerCase() === base);
      if (m) {
        localStorage.setItem("simono_lang", m.code === "en" ? "en" : "it");
        return m.code;
      }
    }
  } catch (err) {
    // Storage or navigator API unavailable — safe to skip.
    if (process.env.NODE_ENV === "development") {
      // eslint-disable-next-line no-console
      console.warn("LanguageSwitcher: auto-detect skipped", err);
    }
  }
  return null;
};

const useClickOutside = (ref, onOutside) => {
  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onOutside();
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [ref, onOutside]);
};

const useInitLanguage = () => {
  const [current, setCurrent] = useState("it");
  useEffect(() => {
    const existing = readCurrentLang();
    setCurrent(existing);
    ensureWidgetLoaded();
    if (existing === "it") {
      const detected = autoDetectOnce();
      if (detected && detected !== "it") {
        setGoogTransCookie(detected);
        window.location.reload();
      }
    }
  }, []);
  return [current, setCurrent];
};

function LanguageOption({ lang, current, onSelect }) {
  return (
    <li>
      <button
        onClick={() => onSelect(lang.code)}
        className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-paper-alt transition-colors text-left"
        data-testid={`lang-option-${lang.code}`}
      >
        <span className="flex items-center gap-2">
          <span>{lang.flag}</span>
          <span className="text-sm">{lang.label}</span>
        </span>
        {lang.code === current && <Check className="w-4 h-4 opacity-60" />}
      </button>
    </li>
  );
}

function LanguageSwitcher({ scrolled }) {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useInitLanguage();
  const ref = useRef(null);
  const close = useCallback(() => setOpen(false), []);
  useClickOutside(ref, close);

  const choose = useCallback((code) => {
    setCurrent(code);
    setOpen(false);
    setGoogTransCookie(code);
    // Also keep the internal IT/EN dictionary (LangContext) in sync so that
    // static React copy switches together with the Google Translate layer.
    try {
      if (code === "it" || code === "en") {
        localStorage.setItem("simono_lang", code);
      } else {
        // For any other language, base copy remains Italian and Google
        // Translate takes over the rendering.
        localStorage.setItem("simono_lang", "it");
      }
    } catch (err) {
      if (process.env.NODE_ENV === "development") {
        // eslint-disable-next-line no-console
        console.warn("LanguageSwitcher: localStorage write failed", err);
      }
    }
    window.location.reload();
  }, [setCurrent]);

  const cur = LANGUAGES.find((l) => l.code === current) || LANGUAGES[0];

  return (
    <div ref={ref} className="relative notranslate" translate="no">
      <button
        onClick={() => setOpen((o) => !o)}
        className={`flex items-center gap-1.5 hover:opacity-60 transition-opacity ${scrolled ? "text-ink" : "text-paper"}`}
        data-testid="language-switcher"
      >
        <Globe className="w-4 h-4" />
        <span className="font-mono text-[0.72rem] uppercase tracking-[0.2em]">
          {cur.flag} {cur.code.toUpperCase().replace("-CN", "")}
        </span>
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-3 w-48 bg-paper text-ink border border-hairline shadow-xl">
          <ul className="max-h-80 overflow-y-auto">
            {LANGUAGES.map((l) => (
              <LanguageOption key={l.code} lang={l} current={current} onSelect={choose} />
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default LanguageSwitcher;
